import { useEffect } from 'react'
import { shallowEqual, useSelector } from 'react-redux'

import FileDownload from 'js-file-download'

import { useAppDispatch } from '../hooks'
import { Schedule } from '../models'

import {
    createSchedule,
    CreateScheduleRequest,
    getScheduleList,
    GetScheduleListActionThunk,
    ScheduleDetailActionThunk,
    GetScheduleListRequest,
    RootState,
    updateSchedule,
    UpdateScheduleRequest,
    GetScheduleDetailRequest,
    getScheduleDetail,
    GET_SCHEDULE_DETAIL_REQUEST,
    setScheduleDetail,
    CREATE_SCHEDULE_REQUEST,
    UPDATE_SCHEDULE_REQUEST,
} from '../store'
import { axiosInstance } from '../helpers'
import { toast } from 'react-toastify'

type UseSchedule = {
    scheduleList: Schedule[]
    scheduleDetail?: Schedule
    loadingDetail: boolean
    loadingCreate: boolean
    loadingUpdate: boolean
    getScheduleList: (req: GetScheduleListRequest) => GetScheduleListActionThunk
    getScheduleDetail: (
        req: GetScheduleDetailRequest,
    ) => ScheduleDetailActionThunk
    createSchedule: (req: CreateScheduleRequest) => ScheduleDetailActionThunk
    updateSchedule: (
        req: UpdateScheduleRequest,
        params?: { portfolio?: boolean },
    ) => ScheduleDetailActionThunk
    getUnitReport: (schedule: Schedule) => Promise<void>
    syncBedStatuses: (schedule: Schedule) => Promise<void>
    syncBedStatusesFromLeases: (schedule: Schedule) => Promise<void>
    archiveProject: (projectId: number) => Promise<boolean>
}

interface Options {
    getScheduleDetail?: number
    cleanUp?: boolean
}

export const useSchedule = (options?: Options): UseSchedule => {
    const dispatch = useAppDispatch()

    const scheduleList = useSelector(
        (state: RootState) => state.workorder.schedule.scheduleList,
        shallowEqual,
    )

    const scheduleDetail = useSelector(
        (state: RootState) => state.workorder.schedule.scheduleDetail,
    )

    const loadingState = useSelector(
        (state: RootState) => state.workorder.schedule.isLoading,
    )

    useEffect(() => {
        if (options?.getScheduleDetail !== undefined) {
            dispatch(
                getScheduleDetail({ scheduleId: options.getScheduleDetail }),
            )
        }

        return () => {
            if (options?.cleanUp) {
                dispatch(setScheduleDetail())
            }
        }
    }, [])

    const getUnitReport = async (schedule: Schedule) => {
        try {
            const res = await axiosInstance.post(
                'stats/workorder-report/',
                {
                    schedule: schedule?.id,
                    variant: 'unit',
                    tz_offset: new Date().getTimezoneOffset(),
                    apartment_id: schedule?.apartment.id,
                },
                { responseType: 'blob' },
            )
            FileDownload(
                new Blob([res.data]),
                `${schedule.name}_unit_report.xlsx`,
            )
        } catch (err) {
            console.log(err)
            toast.error('There was a problem downloading your report')
        }
    }

    const syncBedStatuses = async (schedule: Schedule) => {
        try {
            axiosInstance.get(
                `workorder/schedule/${schedule?.id}/entrata_sync_bed_status/`,
                { params: { portfolio: true } },
            )

            toast.success(
                'Your request is being processed.  We will send you an email as soon as we are finished.',
            )
        } catch (err) {
            toast.error('There was a problem with your request')
        }
    }

    const syncBedStatusesFromLeases = async (schedule: Schedule) => {
        try {
            await axiosInstance.get(
                `workorder/schedule/${schedule?.id}/sync_bed_status_from_leases/`,
                { params: { portfolio: true } },
            )

            toast.success(
                'Bed status sync started — refresh in a minute to see updates.',
            )
        } catch (err: any) {
            const code = err?.response?.data?.code
            if (code === 'PAST_LAST_SYNC_DATE') {
                toast.error(
                    'Last sync date has passed, adjust the last sync date to sync again.',
                )
            } else {
                toast.error('There was a problem with your request')
            }
        }
    }

    const archiveProject = async (projectId: number): Promise<boolean> => {
        try {
            await axiosInstance.post(`workorder/schedule/${projectId}/archive/`)
            toast.success('Project archived successfully')
            return true
        } catch (err) {
            toast.error('There was a problem with your request')
            return false
        }
    }

    return {
        scheduleList: scheduleList,
        scheduleDetail: scheduleDetail,
        loadingDetail: loadingState[GET_SCHEDULE_DETAIL_REQUEST],
        loadingCreate: loadingState[CREATE_SCHEDULE_REQUEST],
        loadingUpdate: loadingState[UPDATE_SCHEDULE_REQUEST],
        getScheduleList: (req) => dispatch(getScheduleList(req)),
        getScheduleDetail: (req) => dispatch(getScheduleDetail(req)),
        createSchedule: (req) => dispatch(createSchedule(req)),
        updateSchedule: (req, params?) => dispatch(updateSchedule(req, params)),
        getUnitReport: getUnitReport,
        syncBedStatuses: syncBedStatuses,
        syncBedStatusesFromLeases: syncBedStatusesFromLeases,
        archiveProject: archiveProject,
    }
}
